import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "../../services/API";

export const fetchMyReports = createAsyncThunk(
    "reportCenter/fetchMyReports",
    async (_, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.get("/moderation/my-reports/");
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Failed to fetch reports" });
        }
    }
);

export const fetchMyViolations = createAsyncThunk(
    "reportCenter/fetchMyViolations",
    async (_, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.get("/moderation/my-violations/");
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Failed to fetch violations" });
        }
    }
);

export const fetchViolationDetail = createAsyncThunk(
    "reportCenter/fetchViolationDetail",
    async (postId, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.get(`/moderation/my-violations/${postId}/`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Failed to fetch violation detail" });
        }
    }
);

export const submitAppeal = createAsyncThunk(
    "reportCenter/submitAppeal",
    async ({ postId, appealText }, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post(`/moderation/my-violations/${postId}/appeal/`, {
                appeal_text: appealText,
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Failed to submit appeal" });
        }
    }
);

export const fetchMyAppeals = createAsyncThunk(
    "reportCenter/fetchMyAppeals",
    async (_, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.get("/moderation/my-appeals/");
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Failed to fetch appeals" });
        }
    }
);

const ReportCenterSlice = createSlice({
    name: "reportCenter",
    initialState: {
        myReports: { data: [], loading: true, error: null },
        myViolations: { data: [], loading: true, error: null },
        violationDetail: { data: null, loading: false, error: null },
        myAppeals: { data: [], loading: true, error: null },
        appeal: { loading: false, error: null, success: false },
    },
    reducers: {
        resetAppealState(state) {
            state.appeal = { loading: false, error: null, success: false };
        },
        resetViolationDetail(state) {
            state.violationDetail = { data: null, loading: false, error: null };
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchMyReports.pending, (state) => {
                state.myReports.loading = true;
                state.myReports.error = null;
            })
            .addCase(fetchMyReports.fulfilled, (state, action) => {
                state.myReports.loading = false;
                state.myReports.data = action.payload?.results ?? action.payload ?? [];
            })
            .addCase(fetchMyReports.rejected, (state, action) => {
                state.myReports.loading = false;
                state.myReports.error = action.payload;
            })

            .addCase(fetchMyViolations.pending, (state) => {
                state.myViolations.loading = true;
                state.myViolations.error = null;
            })
            .addCase(fetchMyViolations.fulfilled, (state, action) => {
                state.myViolations.loading = false;
                state.myViolations.data = action.payload?.results ?? action.payload ?? [];
            })
            .addCase(fetchMyViolations.rejected, (state, action) => {
                state.myViolations.loading = false;
                state.myViolations.error = action.payload;
            })

            .addCase(fetchViolationDetail.pending, (state) => {
                state.violationDetail.loading = true;
                state.violationDetail.error = null;
                state.violationDetail.data = null;
            })
            .addCase(fetchViolationDetail.fulfilled, (state, action) => {
                state.violationDetail.loading = false;
                state.violationDetail.data = action.payload;
            })
            .addCase(fetchViolationDetail.rejected, (state, action) => {
                state.violationDetail.loading = false;
                state.violationDetail.error = action.payload;
            })

            .addCase(fetchMyAppeals.pending, (state) => {
                state.myAppeals.loading = true;
                state.myAppeals.error = null;
            })
            .addCase(fetchMyAppeals.fulfilled, (state, action) => {
                state.myAppeals.loading = false;
                state.myAppeals.data = action.payload?.results ?? action.payload ?? [];
            })
            .addCase(fetchMyAppeals.rejected, (state, action) => {
                state.myAppeals.loading = false;
                state.myAppeals.error = action.payload;
            })

            .addCase(submitAppeal.pending, (state) => {
                state.appeal.loading = true;
                state.appeal.error = null;
                state.appeal.success = false;
            })
            .addCase(submitAppeal.fulfilled, (state) => {
                state.appeal.loading = false;
                state.appeal.success = true;
            })
            .addCase(submitAppeal.rejected, (state, action) => {
                state.appeal.loading = false;
                state.appeal.error = action.payload;
            });
    },
});

export const { resetAppealState, resetViolationDetail } = ReportCenterSlice.actions;
export default ReportCenterSlice.reducer;
